import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import AIAssessmentViewer from '../../components/AIAssessmentViewer';
import Pagination from '../../components/Pagination';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { PatientMeasurementsDisplay } from '../../components/MeasurementDisplay';
import useUnitPreferences from '../../hooks/useUnitPreferences';

const ProgramAdminAnswers = () => {
  const { user } = useAuth();
  const { isInternationalSystem } = useUnitPreferences();
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    protocol: '',
    question: '',
    status: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [protocols, setProtocols] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: 'answered_at',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedCaseDetails, setSelectedCaseDetails] = useState(null);
  const [loadingCaseDetails, setLoadingCaseDetails] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState(null);

  // Load answers and filter options on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch answers, protocols, and questions in parallel
      // IMPORTANT: Use large page size to get ALL data for enrichment
      const [answersResponse, protocolsResponse, questionsResponse] = await Promise.all([
        api.get('/answers'),
        api.get('/protocols?page=1&per_page=1000'),  // Get ALL protocols for enrichment
        api.get('/questions?page=1&per_page=1000')   // Get ALL questions for enrichment
      ]);
      
      // First, get the protocols and questions data
      const protocolsData = protocolsResponse.data?.protocols || [];
      const questionsData = questionsResponse.data?.questions || [];
      
      // Set state for protocols and questions
      setProtocols(protocolsData);
      setQuestions(questionsData);
      
      // Now enrich answers using the retrieved data (not the state)
      if (answersResponse.data && answersResponse.data.answers) {
        const enrichedAnswers = await enrichAnswersWithDetails(
          answersResponse.data.answers,
          questionsData,
          protocolsData
        );
        setAnswers(enrichedAnswers);
      }
    } catch (error) {
      console.error('Error loading answers:', error);
      setError('Failed to load answers. ' + (error.message || ''));
    } finally {
      setLoading(false);
    }
  };

  // Update the enrichAnswersWithDetails function to accept questionsData and protocolsData
  const enrichAnswersWithDetails = async (answersData, questionsData, protocolsData) => {
    try {
      // Get all unique doctor IDs from answers
      const doctorIds = [...new Set(answersData.map(answer => answer.answered_by))];
      
      // Fetch doctor information in a single request if needed
      let doctorsMap = {};
      if (doctorIds.length > 0) {
        const doctorsResponse = await api.get('/doctors');
        if (doctorsResponse.data && doctorsResponse.data.doctors) {
          doctorsMap = doctorsResponse.data.doctors.reduce((acc, doctor) => {
            acc[doctor.id] = doctor;
            return acc;
          }, {});
        }
      }

      // Create maps for faster lookups
      const questionsMap = {};
      questionsData.forEach(question => {
        questionsMap[question.id] = question;
      });

      const protocolsMap = {};
      protocolsData.forEach(protocol => {
        protocolsMap[protocol.id] = protocol;
      });

      // Enrich each answer with related information
      return answersData.map(answer => ({
        ...answer,
        doctor_name: doctorsMap[answer.answered_by]?.name || 'Unknown Doctor',
        question_text: questionsMap[answer.question_id]?.text || 'Unknown Question',
        protocol_name: protocolsMap[answer.protocol_id]?.name || 'Unknown Protocol'
      }));
    } catch (error) {
      console.error('Error enriching answers:', error);
      return answersData;
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Apply filters and search to answers
  const filteredAnswers = answers.filter(answer => {
    // Apply protocol filter
    if (filters.protocol && answer.protocol_id !== parseInt(filters.protocol)) {
      return false;
    }
    
    // Apply question filter
    if (filters.question && answer.question_id !== parseInt(filters.question)) {
      return false;
    }
    
    // Apply search term to answer_text/content, doctor name, or question text
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      // Check for content in both possible field names
      const answerText = answer.content || answer.answer_text || '';
      const contentMatch = answerText.toLowerCase().includes(searchLower);
      const doctorMatch = answer.doctor_name?.toLowerCase().includes(searchLower) || false;
      const questionMatch = answer.question_text?.toLowerCase().includes(searchLower) || false;
      
      if (!contentMatch && !doctorMatch && !questionMatch) {
        return false;
      }
    }
    
    return true;
  });

  // Sort filtered answers
  const sortedAnswers = [...filteredAnswers].sort((a, b) => {
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    
    // Handle special case for nested properties or null values
    if (sortConfig.key === 'question_text' || sortConfig.key === 'protocol_name' || sortConfig.key === 'doctor_name') {
      aValue = aValue || '';
      bValue = bValue || '';
    }
    
    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedAnswers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedAnswers.length / itemsPerPage);

  // Fetch case details by case ID
  const fetchCaseDetails = async (caseId) => {
    try {
      const response = await api.get(`/cases/${caseId}`);
      if (response.data && response.data.case) {
        const caseDetails = response.data.case;

        // Ensure aiResponseData is present - parse from aiResponse if needed
        if (!caseDetails.aiResponseData && caseDetails.aiResponse) {
          try {
            caseDetails.aiResponseData = JSON.parse(caseDetails.aiResponse);
          } catch (e) {
            console.warn('Failed to parse aiResponse:', e);
          }
        }

        return caseDetails;
      }
      return null;
    } catch (error) {
      console.error('Error fetching case details:', error);
      return null;
    }
  };

  // Handle answer details view
  const handleViewAnswer = async (answer) => {
    setSelectedAnswer(answer);
    setSelectedCaseDetails(null);
    setSelectedProtocol(null);
    setLoadingCaseDetails(true);

    try {
      // Fetch case details and protocol information for this answer
      const [caseDetails, protocol] = await Promise.all([
        fetchCaseDetails(answer.case_id),
        fetchProtocolDetails(answer.protocol_id)
      ]);
      setSelectedCaseDetails(caseDetails);
      setSelectedProtocol(protocol);
    } catch (error) {
      console.error('Error loading answer details:', error);
      setSelectedCaseDetails(null);
      setSelectedProtocol(null);
    } finally {
      setLoadingCaseDetails(false);
    }
  };

  // Fetch protocol details by protocol ID
  const fetchProtocolDetails = async (protocolId) => {
    try {
      const response = await api.get(`/protocols/${protocolId}`);
      if (response.data && response.data.protocol) {
        return response.data.protocol;
      }
      return null;
    } catch (error) {
      console.error('Error fetching protocol details:', error);
      return null;
    }
  };

  // Close answer details modal
  const closeAnswerModal = () => {
    setSelectedAnswer(null);
    setSelectedCaseDetails(null);
    setSelectedProtocol(null);
  };




  return (
    <ResearchLayout pageTitle="View Answers">
      <div className="container-fluid">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>
            <i className="fas fa-comment-dots text-primary me-2"></i>
            Question Answers
          </h2>
          <Link to="/program-admin/questions" className="btn btn-outline-primary">
            <i className="fas fa-question-circle me-2"></i>
            Manage Questions
          </Link>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {/* Filters Section */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label htmlFor="protocol" className="form-label">Filter by Protocol</label>
                <select
                  id="protocol"
                  name="protocol"
                  className="form-select"
                  value={filters.protocol}
                  onChange={handleFilterChange}
                >
                  <option value="">All Protocols</option>
                  {protocols.map(protocol => (
                    <option key={protocol.id} value={protocol.id}>
                      {protocol.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="col-md-3">
                <label htmlFor="question" className="form-label">Filter by Question</label>
                <select
                  id="question"
                  name="question"
                  className="form-select"
                  value={filters.question}
                  onChange={handleFilterChange}
                >
                  <option value="">All Questions</option>
                  {questions.map(question => (
                    <option key={question.id} value={question.id}>
                      {question.text.length > 40 ? question.text.slice(0, 40) + '...' : question.text}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="col-md-6">
                <label htmlFor="search" className="form-label">Search</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    id="search"
                    className="form-control"
                    placeholder="Search answers, doctor names, or questions..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm('')}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Answers Table & Mobile Cards */}
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading answers...</span>
                </div>
                <p className="mt-2">Loading answers...</p>
              </div>
            ) : currentItems.length > 0 ? (
              <>
                {/* Desktop Table */}
                <div className="table-responsive d-none d-md-block">
                  <table className="table table-hover align-middle" style={{ tableLayout: 'auto' }}>
                    <thead className="table-light">
                      <tr>
                        <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>
                          ID {sortConfig.key === 'id' && (
                            <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                          )}
                        </th>
                        <th onClick={() => handleSort('question_text')} style={{ cursor: 'pointer' }}>
                          Question {sortConfig.key === 'question_text' && (
                            <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                          )}
                        </th>
                        <th onClick={() => handleSort('content')} style={{ cursor: 'pointer' }}>
                          Answer {sortConfig.key === 'content' && (
                            <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                          )}
                        </th>
                        <th onClick={() => handleSort('protocol_name')} style={{ cursor: 'pointer' }}>
                          Protocol {sortConfig.key === 'protocol_name' && (
                            <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                          )}
                        </th>
                        <th onClick={() => handleSort('doctor_name')} style={{ cursor: 'pointer' }}>
                          Answered By {sortConfig.key === 'doctor_name' && (
                            <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                          )}
                        </th>
                        <th onClick={() => handleSort('answered_at')} style={{ cursor: 'pointer' }}>
                          Date {sortConfig.key === 'answered_at' && (
                            <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                          )}
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map(answer => (
                        <tr key={answer.id}>
                          <td>{answer.id}</td>
                          <td>
                            <div style={{ maxWidth: '300px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              {answer.question_text}
                            </div>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '200px' }} 
                                 title={answer.content || answer.answer_text || ''}>
                              {answer.content || answer.answer_text || ''}
                            </div>
                          </td>
                          <td>{answer.protocol_name}</td>
                          <td>{answer.doctor_name}</td>
                          <td>
                            {answer.answered_at ? new Date(answer.answered_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => handleViewAnswer(answer)}
                            >
                              <i className="fas fa-eye me-1"></i> View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile Cards */}
                <div className="d-block d-md-none">
                  {currentItems.map(answer => (
                    <div key={answer.id} className="card mb-3 border border-primary">
                      <div className="card-body p-3">
                        <div className="mb-2">
                          <span className="badge bg-primary me-2">#{answer.id}</span>
                          <span className="badge bg-info">{answer.protocol_name}</span>
                        </div>
                        <div className="mb-2">
                          <strong>Question:</strong>
                          <div className="small text-muted" style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {answer.question_text}
                          </div>
                        </div>
                        <div className="mb-2">
                          <strong>Answer:</strong>
                          <div>{answer.content || answer.answer_text || ''}</div>
                        </div>
                        <div className="mb-2">
                          <strong>Doctor:</strong> <span className="badge bg-secondary">{answer.doctor_name}</span>
                        </div>
                        <div className="mb-2">
                          <strong>Date:</strong> {answer.answered_at ? new Date(answer.answered_at).toLocaleDateString() : 'N/A'}
                        </div>
                        <div>
                          <button
                            className="btn btn-sm btn-outline-info w-100"
                            onClick={() => handleViewAnswer(answer)}
                          >
                            <i className="fas fa-eye me-1"></i> View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  showInfo={true}
                  totalItems={sortedAnswers.length}
                  itemsPerPage={itemsPerPage}
                  className="mt-3"
                />
              </>
            ) : (
              <div className="text-center py-5">
                <i className="fas fa-comment-dots fa-3x text-muted mb-3"></i>
                <h4>No Answers Found</h4>
                <p className="text-muted">
                  {searchTerm || filters.protocol || filters.question ? 
                    'No answers match your search criteria.' : 
                    'No answers have been submitted yet for your questions.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Answer Detail Modal with Side-by-Side Case Details */}
        {selectedAnswer && (
          <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header bg-info text-white">
                  <h5 className="modal-title">
                    <i className="fas fa-search-plus me-2"></i>
                    Answer & Case Details
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeAnswerModal}
                  ></button>
                </div>
                <div className="modal-body p-0">
                  <div className="row g-0">
                    {/* Left Side - Answer Details */}
                    <div className="col-lg-6 border-end">
                      <div className="p-4">
                        <h6 className="text-primary mb-3">
                          <i className="fas fa-comment-dots me-2"></i>
                          Answer Information
                        </h6>
                        
                        <div className="mb-3">
                          <div className="card bg-light">
                            <div className="card-body">
                              <h6 className="card-title text-info mb-2">Question:</h6>
                              <p className="card-text">{selectedAnswer.question_text}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <div className="card">
                            <div className="card-body">
                              <h6 className="card-title mb-2">Answer:</h6>
                              <p className="card-text">{selectedAnswer.content || selectedAnswer.answer_text || ''}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-12 mb-3">
                            <h6 className="text-muted">Answer Details</h6>
                            <ul className="list-group list-group-flush">
                              <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                <span><i className="fas fa-cog me-2 text-primary"></i>Protocol</span>
                                <span className="badge bg-primary">{selectedAnswer.protocol_name}</span>
                              </li>
                              <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                <span><i className="fas fa-id-card me-2 text-primary"></i>Case ID</span>
                                <span className="badge bg-secondary">{selectedAnswer.case_id}</span>
                              </li>
                              <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                <span><i className="fas fa-user-md me-2 text-primary"></i>Doctor</span>
                                <span className="badge bg-info">{selectedAnswer.doctor_name}</span>
                              </li>
                              <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                <span><i className="fas fa-calendar me-2 text-primary"></i>Date Answered</span>
                                <span className="badge bg-success">
                                  {selectedAnswer.answered_at ?
                                    new Date(selectedAnswer.answered_at).toLocaleDateString() + ' ' +
                                    new Date(selectedAnswer.answered_at).toLocaleTimeString() :
                                    'N/A'}
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Right Side - Case Details (Doctor's View) */}
                    <div className="col-lg-6">
                      <div className="p-4">
                        <h6 className="text-success mb-3">
                          <i className="fas fa-user-injured me-2"></i>
                          Case Assessment
                        </h6>
                        
                        {loadingCaseDetails ? (
                          <div className="text-center py-4">
                            <div className="spinner-border text-success" role="status">
                              <span className="visually-hidden">Loading case details...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading case details...</p>
                          </div>
                        ) : selectedCaseDetails ? (
                          <div>
                            {/* Basic Demographics */}
                            <div className="mb-4">
                              <h6 className="text-success border-bottom pb-1 mb-3">Patient Demographics</h6>
                              <div className="row">
                                <div className="col-6 mb-2">
                                  <small className="text-muted">Case ID</small>
                                  <div className="fw-bold">{selectedCaseDetails.customerId || selectedCaseDetails.customer_id}</div>
                                </div>
                                {selectedCaseDetails.score && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Health Score</small>
                                    <div className={`fw-bold ${selectedCaseDetails.score >= 70 ? 'text-success' : selectedCaseDetails.score >= 50 ? 'text-warning' : 'text-danger'}`}>
                                      {selectedCaseDetails.score}
                                    </div>
                                  </div>
                                )}
                                {selectedCaseDetails.age && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Age</small>
                                    <div className="fw-bold">{selectedCaseDetails.age} years</div>
                                  </div>
                                )}
                                {selectedCaseDetails.gender && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Gender</small>
                                    <div className="fw-bold">{selectedCaseDetails.gender}</div>
                                  </div>
                                )}
                                {selectedCaseDetails.highestEducation && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Education</small>
                                    <div className="fw-bold">{selectedCaseDetails.highestEducation}</div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Patient Measurements */}
                            <div className="mb-4">
                              <PatientMeasurementsDisplay
                                patientData={selectedCaseDetails}
                                showVitalSigns={true}
                                showPhysicalMeasurements={true}
                              />
                            </div>

                            {/* Lifestyle Factors */}
                            <div className="mb-4">
                              <h6 className="text-success border-bottom pb-1 mb-3">Lifestyle Assessment</h6>
                              <div className="row">
                                <div className="col-6 mb-2">
                                  <small className="text-muted">Smoking Status</small>
                                  <div className="fw-bold">{selectedCaseDetails.isSmoker ? 'Yes' : 'No'}</div>
                                  {selectedCaseDetails.cigarettesPerDay > 0 && (
                                    <small className="text-muted d-block">{selectedCaseDetails.cigarettesPerDay} cigarettes/day</small>
                                  )}
                                </div>
                                <div className="col-6 mb-2">
                                  <small className="text-muted">Alcohol Consumption</small>
                                  <div className="fw-bold">{selectedCaseDetails.isDrinker ? 'Yes' : 'No'}</div>
                                  {selectedCaseDetails.bottlesPerDay > 0 && (
                                    <small className="text-muted d-block">{selectedCaseDetails.bottlesPerDay} bottles/day</small>
                                  )}
                                </div>
                                {selectedCaseDetails.activeDailyLifestyleLevel && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Activity Level</small>
                                    <div className="fw-bold">{selectedCaseDetails.activeDailyLifestyleLevel}</div>
                                  </div>
                                )}
                                {selectedCaseDetails.wellSleepLevel && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Sleep Quality</small>
                                    <div className="fw-bold">{selectedCaseDetails.wellSleepLevel}</div>
                                  </div>
                                )}
                                {selectedCaseDetails.stressFrequency && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Stress Level</small>
                                    <div className="fw-bold">{selectedCaseDetails.stressFrequency}</div>
                                  </div>
                                )}
                                {selectedCaseDetails.mood && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Mood</small>
                                    <div className="fw-bold">{selectedCaseDetails.mood}</div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* AI Assessment - Always show if available (admin should see what doctor saw) */}
                            {selectedCaseDetails.aiResponseData && Object.keys(selectedCaseDetails.aiResponseData).length > 0 && (
                              <div className="mb-4">
                                <h6 className="text-success border-bottom pb-1 mb-3">
                                  <i className="fas fa-robot me-2"></i>
                                  AI Assessment
                                </h6>
                                <AIAssessmentViewer data={selectedCaseDetails.aiResponseData} />
                              </div>
                            )}

                            {/* Health Predictions */}
                            {(selectedCaseDetails.physicalHealthPrediction ||
                              selectedCaseDetails.clinicalHealthPrediction ||
                              selectedCaseDetails.lifestylePrediction ||
                              selectedCaseDetails.mentalHealthPrediction) && (
                              <div className="mb-4">
                                <h6 className="text-success border-bottom pb-1 mb-3">AI Health Predictions</h6>
                                <div className="row">
                                  {selectedCaseDetails.physicalHealthPrediction && (
                                    <div className="col-6 mb-2">
                                      <small className="text-muted">Physical Health</small>
                                      <div className="fw-bold">{selectedCaseDetails.physicalHealthPrediction}</div>
                                    </div>
                                  )}
                                  {selectedCaseDetails.clinicalHealthPrediction && (
                                    <div className="col-6 mb-2">
                                      <small className="text-muted">Clinical Health</small>
                                      <div className="fw-bold">{selectedCaseDetails.clinicalHealthPrediction}</div>
                                    </div>
                                  )}
                                  {selectedCaseDetails.lifestylePrediction && (
                                    <div className="col-6 mb-2">
                                      <small className="text-muted">Lifestyle</small>
                                      <div className="fw-bold">{selectedCaseDetails.lifestylePrediction}</div>
                                    </div>
                                  )}
                                  {selectedCaseDetails.mentalHealthPrediction && (
                                    <div className="col-6 mb-2">
                                      <small className="text-muted">Mental Health</small>
                                      <div className="fw-bold">{selectedCaseDetails.mentalHealthPrediction}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Recent Symptoms */}
                            {(selectedCaseDetails.lastMonthPain || selectedCaseDetails.lastMonthDiminishedVision || selectedCaseDetails.lastMonthDiminishedHearing) && (
                              <div className="mb-4">
                                <h6 className="text-success border-bottom pb-1 mb-3">Recent Symptoms (Last Month)</h6>
                                <div className="row">
                                  {selectedCaseDetails.lastMonthPain && (
                                    <div className="col-12 mb-2">
                                      <small className="text-muted">Pain</small>
                                      <div className="small">{selectedCaseDetails.lastMonthPain}</div>
                                    </div>
                                  )}
                                  {selectedCaseDetails.lastMonthDiminishedVision && (
                                    <div className="col-6 mb-2">
                                      <small className="text-muted">Diminished Vision</small>
                                      <div className="small">{selectedCaseDetails.lastMonthDiminishedVision}</div>
                                    </div>
                                  )}
                                  {selectedCaseDetails.lastMonthDiminishedHearing && (
                                    <div className="col-6 mb-2">
                                      <small className="text-muted">Diminished Hearing</small>
                                      <div className="small">{selectedCaseDetails.lastMonthDiminishedHearing}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Medical History */}
                            {(selectedCaseDetails.diagnosedChronicDisease || selectedCaseDetails.familyMemberDiagnosedChronicDisease) && (
                              <div className="mb-4">
                                <h6 className="text-success border-bottom pb-1 mb-3">Medical History</h6>
                                <div className="row">
                                  {selectedCaseDetails.diagnosedChronicDisease && (
                                    <div className="col-12 mb-2">
                                      <small className="text-muted">Personal Medical History</small>
                                      <div className="small">{selectedCaseDetails.diagnosedChronicDisease}</div>
                                    </div>
                                  )}
                                  {selectedCaseDetails.familyMemberDiagnosedChronicDisease && (
                                    <div className="col-12 mb-2">
                                      <small className="text-muted">Family Medical History</small>
                                      <div className="small">{selectedCaseDetails.familyMemberDiagnosedChronicDisease}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Assessment Metadata */}
                            <div className="mb-4">
                              <h6 className="text-success border-bottom pb-1 mb-3">Assessment Information</h6>
                              <div className="row">
                                {selectedCaseDetails.recommendedInsightId && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Recommended Insight ID</small>
                                    <div className="small">{selectedCaseDetails.recommendedInsightId}</div>
                                  </div>
                                )}
                                {selectedCaseDetails.color && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Assessment Color</small>
                                    <div className="small">{selectedCaseDetails.color}</div>
                                  </div>
                                )}
                                {selectedCaseDetails.createdAt && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Created</small>
                                    <div className="small">{new Date(selectedCaseDetails.createdAt).toLocaleDateString()}</div>
                                  </div>
                                )}
                                {selectedCaseDetails.updatedAt && (
                                  <div className="col-6 mb-2">
                                    <small className="text-muted">Last Updated</small>
                                    <div className="small">{new Date(selectedCaseDetails.updatedAt).toLocaleDateString()}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <i className="fas fa-exclamation-triangle text-warning fa-2x mb-2"></i>
                            <p className="text-muted">Unable to load case details</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeAnswerModal}
                  >
                    <i className="fas fa-times me-1"></i>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default ProgramAdminAnswers;